import {
  Button,
  Card,
  HStack,
  MenuItem,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  BasisAdjustmentsTableRow,
  FeePostUseBreakdownBasisAdjustmentsTable,
  makeEmptyBasisAdjustmentsRow,
} from "features/fees/post/use-breakdown/FeePostUseBreakdownBasisAdjustmentsTable";
import { FeePostUseBreakdownBasisChangeTable } from "features/fees/post/use-breakdown/FeePostUseBreakdownBasisChangeTable";
import {
  getFeeChargeEventBasisChangeByUseRetrieveQueryKey,
  useFeeChargeEventBasisStatsRetrieve,
  useFeeChargeEventPointInTimeTransactionCodesListSuspense,
  useFeeChargeEventsSelectListSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import { getFeeStatsRetrieveQueryKey } from "gen/endpoints/fee-stats/fee-stats";
import {
  getPortfolioTransactionListQueryKey,
  getPortfolioTransactionReadOnlyTableRetriveQueryKey,
  usePortfolioTransactionListSuspense,
  usePortfolioTransactionReadOnlyTableRetriveSuspense,
  usePortfolioTransactionUpsert,
} from "gen/endpoints/portfolio-transactions/portfolio-transactions";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { PortfolioTransactionBulkCreateDataRequest } from "gen/model/portfolioTransactionBulkCreateDataRequest";
import { get } from "lodash-es";
import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { Restricted } from "shared/components/access/Restricted";
import { DropdownButton } from "shared/components/button/DropdownButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { CardSectionBody } from "shared/components/card/CardSection";
import Form from "shared/components/form/Form";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { MaybernTableGetErrorForCellFn } from "shared/components/table/maybern/MaybernTableErrors";
import { useMaybernTableFormRowController } from "shared/components/table/maybern/MaybernTableFormRowController";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { getApiErrorFromResponse } from "shared/utils/ErrorUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

const FeeBasisTable: React.FC<{
  feeChargeEventSelected: string;
}> = ({ feeChargeEventSelected }) => {
  const { t } = useTranslation(["fees", "common"]);
  const { fundFamilyId } = useRequiredStringParams();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const { data: portfolioTransactionData } =
    usePortfolioTransactionReadOnlyTableRetriveSuspense(fundFamilyId);
  const { data: portfolioTransactionListData } =
    usePortfolioTransactionListSuspense(fundFamilyId, {
      include_only_drafts: true,
    });

  const form = useForm<PortfolioTransactionBulkCreateDataRequest>({
    defaultValues: {
      portfolio_transactions: portfolioTransactionListData.length
        ? portfolioTransactionListData
        : [makeEmptyBasisAdjustmentsRow()],
    },
  });

  const { propsForRows } = useMaybernTableFormRowController(
    form,
    "portfolio_transactions",
  );

  const {
    mutateAsync: mutatePortfolioTransactionUpsert,
    error: upsertError,
    variables: upsertVariables,
  } = usePortfolioTransactionUpsert({
    mutation: {
      onSuccess: async () => {
        await invalidateQueryKeys(
          queryClient,
          getFeeStatsRetrieveQueryKey(fundFamilyId),
          getPortfolioTransactionReadOnlyTableRetriveQueryKey(fundFamilyId),
          getPortfolioTransactionListQueryKey(fundFamilyId),
          getFeeChargeEventBasisChangeByUseRetrieveQueryKey(
            fundFamilyId,
            feeChargeEventSelected,
          ),
        );
      },
    },
  });

  const getErrorForCell = useCallback<
    MaybernTableGetErrorForCellFn<BasisAdjustmentsTableRow>
  >(
    (params) => {
      const errorData = getApiErrorFromResponse(upsertError)?.data;
      if (!errorData) {
        return undefined;
      }

      // ensure that errors are displayed in the correct row after other rows are added or removed
      const indexWhenSubmitted =
        upsertVariables?.data.portfolio_transactions.findIndex(
          // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
          (row) => get(row, "id") === params.node.id,
        ) ?? -1;
      if (indexWhenSubmitted === -1) {
        return undefined;
      }

      const key = `portfolio_transactions.${indexWhenSubmitted}.${params.column!.getColId()}`;

      // don't show error if the value has changed since submission
      if (upsertVariables) {
        const submittedValue = get(upsertVariables.data, key);
        const currentValue = get(
          params.node.data,
          params.column!.getColId(),
        ) as string | undefined;
        if (submittedValue !== currentValue) {
          return undefined;
        }
      }

      const fieldErrors = get(errorData, key) as string[] | undefined;
      return fieldErrors?.join(", ");
    },
    [upsertVariables, upsertError],
  );

  const onSubmitFinalize = async () => {
    await mutatePortfolioTransactionUpsert({
      fundFamilyId,
      data: {
        portfolio_transactions: propsForRows.rowData!,
      },
      params: {
        finalize_event: true,
      },
    });
  };

  const onSubmitDraft = async () => {
    await mutatePortfolioTransactionUpsert({
      fundFamilyId,
      data: {
        portfolio_transactions: propsForRows.rowData!,
      },
      params: {
        finalize_event: false,
      },
    });
  };

  const onSuccess = () => {
    form.reset({
      portfolio_transactions: [makeEmptyBasisAdjustmentsRow()],
    });
    setIsEditing(false);
  };

  return (
    <Form
      form={form}
      onSubmit={onSubmitFinalize}
      onSaveProgress={onSubmitDraft}
      onSuccess={onSuccess}
      onError={() => true}
    >
      <TableBox>
        <TableBoxHeader>
          <HStack>
            <TableBoxTitle>
              {t("fees:feeUseBreakdown.basisAdjustmentsTable.title")}
            </TableBoxTitle>
            <Restricted permission={PermissionTypeEnum.write}>
              <DropdownButton
                title={
                  isEditing
                    ? "Edit"
                    : t("fees:feeUseBreakdown.basisAdjustmentsTable.view")
                }
                colorScheme="tertiary"
                size="xs"
              >
                <MenuItem onClick={() => setIsEditing(true)}>Edit</MenuItem>
                <MenuItem onClick={() => setIsEditing(false)}>View</MenuItem>
              </DropdownButton>
            </Restricted>
          </HStack>
          <DataTableBoxActions>
            <Restricted permission={PermissionTypeEnum.write}>
              {isEditing ? (
                <Button
                  size="xs"
                  colorScheme="tertiary"
                  onClick={onSubmitDraft}
                >
                  {t("common:refresh")}
                  <Tooltip
                    key="refreshToolTip"
                    label={t("fees:feeBasisTab.tool_tip")}
                  >
                    <InfoCircleIcon
                      w="12px"
                      h="12px"
                      ml="10px"
                      color="gray.400"
                    />
                  </Tooltip>
                </Button>
              ) : null}
            </Restricted>
            <Restricted permission={PermissionTypeEnum.approve}>
              {isEditing ? (
                <FormSubmitButton size="xs">
                  {t("fees:feeBasisTab.finalize")}
                </FormSubmitButton>
              ) : null}
            </Restricted>
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          {isEditing ? (
            <QueryBoundary>
              <FeePostUseBreakdownBasisAdjustmentsTable
                fundFamilyId={fundFamilyId}
                getErrorForCell={getErrorForCell}
                form={form}
              />
            </QueryBoundary>
          ) : (
            <DataTable data={portfolioTransactionData} />
          )}
        </TableBoxBody>
      </TableBox>
    </Form>
  );
};

const FeeBasisStats: React.FC<{
  feeChargeEventSelected: string;
}> = ({ feeChargeEventSelected }) => {
  const { fundFamilyId } = useRequiredStringParams();

  const { data: statsData } = useFeeChargeEventBasisStatsRetrieve(
    fundFamilyId,
    feeChargeEventSelected,
  );

  return (
    <VStack spacing="12px" mb="12px" alignItems="stretch">
      {statsData?.basis_id_to_basis_stats &&
        Object.values(statsData.basis_id_to_basis_stats).map((basis_stats) => (
          <Card variant="stat" key={basis_stats.name}>
            <CardSectionBody mb="16px" mt="16px">
              <StatGroup alignItems="center" justifyItems="center" ml="24px">
                <Stat size="sm">
                  <StatNumber fontSize="24px">
                    {formatMoney(basis_stats.total_basis)}
                  </StatNumber>
                  <StatLabel>{basis_stats.name}</StatLabel>
                </Stat>
                {Object.values(basis_stats.basis_category_to_stats).map(
                  (basis_category_stats) => (
                    <Stat size="sm" key={basis_category_stats.name}>
                      <StatNumber fontSize="16px">
                        {formatMoney(basis_category_stats.total_basis)}
                      </StatNumber>
                      <StatLabel>{basis_category_stats.name}</StatLabel>
                    </Stat>
                  ),
                )}
              </StatGroup>
            </CardSectionBody>
          </Card>
        ))}
    </VStack>
  );
};

export const FeesBasisTab: React.FC = () => {
  const { fundFamilyId } = useRequiredStringParams();

  const { data: feePeriodSelectData } =
    useFeeChargeEventsSelectListSuspense(fundFamilyId);
  const [feeChargeEventSelected, setFeeChargeEventSelected] = useState(
    feePeriodSelectData.length
      ? feePeriodSelectData[feePeriodSelectData.length - 1].value
      : "",
  );

  const { data: transactionCodeList } =
    useFeeChargeEventPointInTimeTransactionCodesListSuspense(fundFamilyId);

  return (
    <PageTabBody isFullHeight={true}>
      <FeeBasisStats feeChargeEventSelected={feeChargeEventSelected} />
      {/* Hide the FeeBasisTable if there are no codes configured to adjust basis */}
      {!transactionCodeList.length && (
        <FeeBasisTable feeChargeEventSelected={feeChargeEventSelected} />
      )}
      <FeePostUseBreakdownBasisChangeTable
        fundFamilyId={fundFamilyId}
        feeChargeEventId={feeChargeEventSelected}
        setFeeChargeEventSelected={setFeeChargeEventSelected}
        feePeriodSelectData={feePeriodSelectData}
      />
    </PageTabBody>
  );
};
